import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log, statusTrace } from '../utils/DebugLogger';
import { updatePeriodStatus, PERIOD_STATUS } from './usePayrollPeriods';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_LinkExpensesInput.ts';
import '../contracts/Payroll_FranchiseIdInput.ts';
import '../contracts/Payroll_PayDespesaInput.ts';
import '../contracts/Payroll_AvailableExpensesResponse';
import '../contracts/Payroll_CalculateCommissionOverGrossSoldRemotelyResponse';
import '../contracts/Payroll_CalculateFixedResponse';
import '../contracts/Payroll_LinkExpensesResponse';
import '../contracts/Payroll_MarkPeriodCalculatedResponse';
import '../contracts/Payroll_PayDespesaResponse';
import '../contracts/Payroll_PayPeriodResponse';
import '../contracts/Payroll_PeriodCalculationSummaryResponse';
import '../contracts/Payroll_PeriodPaymentsResponse';
import '../contracts/Payroll_PeriodResponse';

import '../contracts/Payroll_CalculateCommissionOverGrossExecutedResponse';
import '../contracts/Payroll_ConfirmPeriodResponse';
import '../contracts/Payroll_PaymentRulesResponse';
import type { Payroll_FranchiseIdInput } from '../contracts/Payroll_FranchiseIdInput';
import type { Payroll_LinkExpensesInput } from '../contracts/Payroll_LinkExpensesInput';
import type { Payroll_PayDespesaInput } from '../contracts/Payroll_PayDespesaInput';
/**
 * Composable for payroll calculations and operations
 */
export function usePayrollCalculations() {
  const duePayments = ref<any>(null);
  const availableExpenses = ref<any[]>([]);
  const calculations = ref<any>(null);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Discover due payments for a franchise
   * @param {number} franchiseId
   * @param {string} frequency - Optional frequency filter (weekly, biweekly, monthly)
   * @returns {Promise<Object>}
   */
  const discoverDuePayments = async (franchiseId: any, frequency = null) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'discoverDuePayments', 'Discovering due payments', {
      franchiseId,
      frequency
    });

    try {
      const params: any = { franchise_id: franchiseId };
      if (frequency) params.frequency = frequency;

      // Backend answers Payroll_DiscoverDuePaymentsResponse (restored 2026-09-01):
      // { weekly, biweekly, monthly } buckets of counts + window + total.
      const response = await httpService.get<ApiResponse<unknown>>('/payroll/discover_due_payments', {
        params,
        responseContract: 'Payroll_DiscoverDuePaymentsResponse',
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to discover due payments');
      }

      duePayments.value = response.dados;
      log('usePayrollCalculations', 'discoverDuePayments', 'Due payments discovered', {
        count: duePayments.value ? Object.keys(duePayments.value).length : 0
      });

      return duePayments.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'discoverDuePayments', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Complete payment for a period
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>}
   */
  const completePayment = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'completePayment', 'Completing payment', {
      periodId,
      franchiseId
    });

    try {
      const payPeriodPayload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payPeriodPayload);
      const response = await httpService.post(`/payroll/pay_period/${periodId}`, payPeriodPayload, {
        responseContract: 'Payroll_PayPeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to complete payment');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'completePayment', 'Payment completed', { periodId });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'completePayment', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get available expenses for linking to a period
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Array>}
   */
  const getAvailableExpenses = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'getAvailableExpenses', 'Fetching available expenses', {
      periodId,
      franchiseId
    });

    try {
      const response = await httpService.get(`/payroll/available_expenses/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_AvailableExpensesResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get available expenses');
      }

      availableExpenses.value = response.dados ?? [];
      log('usePayrollCalculations', 'getAvailableExpenses', 'Expenses loaded', {
        count: availableExpenses.value.length
      });

      return availableExpenses.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'getAvailableExpenses', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Link expenses to a payroll period
   * @param {number} periodId
   * @param {Array} expenseIds
   * @param {number} franchiseId
   * @returns {Promise<boolean>}
   */
  const linkExpenses = async (periodId: any, expenseIds: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'linkExpenses', 'Linking expenses', {
      periodId,
      expenseIds,
      franchiseId
    });

    try {
      const payload: Payroll_LinkExpensesInput = {
        franchise_id: franchiseId,
        expense_ids: expenseIds
      };

      validate('Payroll_LinkExpensesInput', payload);
      const response = await httpService.post(`/payroll/link_expenses/${periodId}`, payload, {
        responseContract: 'Payroll_LinkExpensesResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to link expenses');
      }

      log('usePayrollCalculations', 'linkExpenses', 'Expenses linked', {
        periodId,
        count: expenseIds.length
      });

      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'linkExpenses', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // =========================================================================
  // STEP 5: PAYMENT RULES CONCILIATION - NEW SEQUENTIAL FLOW
  // =========================================================================

  /**
   * Step 0: Get payment rules for a period
   * Lists all payment rules that apply to this payroll period
   * Uses a two-step process:
   *   1. Fetch period details to get the frequency
   *   2. Fetch rules by franchise and frequency
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { rules: [...], period: {...} }
   */
  const getPaymentRulesForPeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'getPaymentRulesForPeriod', 'Getting payment rules for period', {
      periodId,
      franchiseId
    });

    try {
      // Step 1: Get period details to obtain the frequency
      const periodResponse = await httpService.get(`/payroll/period/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_PeriodResponse'
      });

      if (!periodResponse.success) {
        throw new Error(periodResponse.error || 'Failed to get period details');
      }

      const period = periodResponse.dados;
      // `frequency` is present on the wire but not declared on the typed contract;
      // accessed via index-typed view until the contract surfaces it.
      const frequency = (period as { frequency?: string } | undefined)?.frequency;

      if (!frequency) {
        throw new Error('Period does not have a frequency defined');
      }

      log('usePayrollCalculations', 'getPaymentRulesForPeriod', 'Period loaded', {
        periodId,
        frequency
      });

      // Step 2: Get rules by franchise and frequency
      const rulesResponse = await httpService.get(`/payroll/payment_rules_by_franchise_and_frequency/${franchiseId}/${frequency}`, { responseContract: 'Payroll_PaymentRulesResponse' });

      if (!rulesResponse.success) {
        throw new Error(rulesResponse.error || 'Failed to get payment rules');
      }

      const rules = rulesResponse.dados?.items ?? [];

      log('usePayrollCalculations', 'getPaymentRulesForPeriod', 'Rules loaded', {
        ruleCount: rules.length
      });

      // Return combined result matching expected structure
      return {
        rules: rules,
        period: period
      };
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'getPaymentRulesForPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Step 1: Calculate commission over gross revenue EXECUTED
   * Calculates commissions for rules where employee is paid based on revenue THEY EXECUTED
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { executions: [...], totals: {...} }
   */
  const calculateCommissionOverGrossExecuted = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'calculateCommissionOverGrossExecuted', 'Calculating commission over executed revenue', {
      periodId,
      franchiseId
    });

    try {
      const calcExecutedPayload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', calcExecutedPayload);
      const response = await httpService.post(`/payroll/calculate_commission_over_gross_executed/${periodId}`, calcExecutedPayload, { responseContract: 'Payroll_CalculateCommissionOverGrossExecutedResponse' });

      if (!response.success) {
        throw new Error(response.error || 'Failed to calculate commission over executed revenue');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'calculateCommissionOverGrossExecuted', 'Commission calculated', {
        calculationCount: result?.calculations?.length || 0,
        totalAmount: result?.totals?.commission_over_gross_executed || 0
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'calculateCommissionOverGrossExecuted', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Step 2: Calculate commission over gross revenue SOLD REMOTELY
   * Calculates commissions for rules where employee is paid based on revenue THEY SOLD remotely
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { executions: [...], totals: {...} }
   */
  const calculateCommissionOverGrossSoldRemotely = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'calculateCommissionOverGrossSoldRemotely', 'Calculating commission over sold remotely revenue', {
      periodId,
      franchiseId
    });

    try {
      const calcSoldRemotelyPayload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', calcSoldRemotelyPayload);
      const response = await httpService.post(`/payroll/calculate_commission_over_gross_sold_remotely/${periodId}`, calcSoldRemotelyPayload, {
        responseContract: 'Payroll_CalculateCommissionOverGrossSoldRemotelyResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to calculate commission over sold remotely revenue');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'calculateCommissionOverGrossSoldRemotely', 'Commission calculated', {
        successCount: result?.success_count ?? 0,
        errorCount: result?.error_count ?? 0
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'calculateCommissionOverGrossSoldRemotely', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Step 3: Calculate fixed rules
   * Creates/updates payment_rule_execution records for fixed salary rules
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { executions: [...], totals: {...} }
   */
  const calculateFixed = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'calculateFixed', 'Calculating fixed rules', {
      periodId,
      franchiseId
    });

    try {
      const calcFixedPayload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', calcFixedPayload);
      const response = await httpService.post(`/payroll/calculate_fixed/${periodId}`, calcFixedPayload, {
        responseContract: 'Payroll_CalculateFixedResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to calculate fixed rules');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'calculateFixed', 'Fixed rules calculated', {
        successCount: result?.success_count ?? 0,
        errorCount: result?.error_count ?? 0
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'calculateFixed', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Mark period as calculated (status = 3)
   * Called after all payment rule calculations are complete
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { period_id, status, status_label, message }
   */
  const markPeriodCalculated = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    // STATUS_TRACE: API call to mark period as calculated
    statusTrace('usePayrollCalculations.markPeriodCalculated', 'BEFORE_API_CALL', {
      periodId,
      franchiseId
    });

    log('usePayrollCalculations', 'markPeriodCalculated', 'Marking period as calculated', {
      periodId,
      franchiseId
    });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/mark_period_calculated/${periodId}`, payload, {
        responseContract: 'Payroll_MarkPeriodCalculatedResponse'
      });

      if (!response.success) {
        // STATUS_TRACE: API returned success=false
        statusTrace('usePayrollCalculations.markPeriodCalculated', 'API_RETURNED_FAILURE', {
          periodId,
          status: undefined,
          error: response.error
        });
        throw new Error(response.error || 'Failed to mark period as calculated');
      }

      const result = response.dados;

      // Sync status to centralized store from API response
      const newStatus = result?.status ?? PERIOD_STATUS.CALCULATED;

      // STATUS_TRACE: API succeeded - about to update store
      statusTrace('usePayrollCalculations.markPeriodCalculated', 'API_SUCCESS', {
        periodId,
        status: newStatus,
        apiResultStatus: result?.status
      });

      updatePeriodStatus(periodId, newStatus, 'mark_period_calculated_api');

      log('usePayrollCalculations', 'markPeriodCalculated', 'Period marked as calculated', {
        periodId,
        status: newStatus,
        statusLabel: result?.status_label
      });

      return result;
    } catch (err) {
      // STATUS_TRACE: API error
      statusTrace('usePayrollCalculations.markPeriodCalculated', 'API_ERROR', {
        periodId,
        status: undefined,
        error: err?.message || String(err)
      });

      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'markPeriodCalculated', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get period calculation summary
   * Returns all executions and totals for the period (for preview/display)
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { by_user: [...], totals: {...} }
   */
  const getPeriodCalculationSummary = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'getPeriodCalculationSummary', 'Getting calculation summary', {
      periodId,
      franchiseId
    });

    try {
      const response = await httpService.get(`/payroll/period_calculation_summary/${periodId}/${franchiseId}`, {
        responseContract: 'Payroll_PeriodCalculationSummaryResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get period calculation summary');
      }

      const result = response.dados;
      const summaryView = result?.summary as { by_user?: unknown[] } | undefined;
      log('usePayrollCalculations', 'getPeriodCalculationSummary', 'Summary loaded', {
        userCount: summaryView?.by_user?.length || 0
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'getPeriodCalculationSummary', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Step 4: Confirm period and create consolidated despesas
   * Creates ONE despesa per user with status 1 (open/draft)
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { despesas: [...], period: {...} }
   */
  const confirmPeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'confirmPeriod', 'Confirming period and creating despesas', {
      periodId,
      franchiseId
    });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/confirm_period/${periodId}`, payload, { responseContract: 'Payroll_ConfirmPeriodResponse' });

      if (!response.success) {
        throw new Error(response.error || 'Failed to confirm period');
      }

      const result = response.dados;

      // Sync status to centralized store from API response
      const newStatus = result?.status ?? PERIOD_STATUS.CONFIRMED;
      updatePeriodStatus(periodId, newStatus, 'confirm_period_api');

      log('usePayrollCalculations', 'confirmPeriod', 'Period confirmed, despesas created with status 1', {
        periodId,
        despesaCount: result?.despesas?.length || 0,
        status: newStatus
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'confirmPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get period payments list (consolidated despesas)
   * Phase D of Step 5: Returns all salary despesas for the period
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} { payments: [...], period: {...} }
   */
  const getPeriodPayments = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'getPeriodPayments', 'Getting period payments', {
      periodId,
      franchiseId
    });

    try {
      const response = await httpService.get(`/payroll/period_payments/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_PeriodPaymentsResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get period payments');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'getPeriodPayments', 'Payments loaded', {
        paymentCount: result?.payments?.length || 0
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'getPeriodPayments', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Pay individual despesa (mark as paid with receipt)
   * Phase D of Step 5: Marks a single despesa as paid
   * @param {number} despesaId
   * @param {number} franchiseId
   * @param {string} paymentDate - Y-m-d format
   * @param {string|null} comprovanteUrl - Optional receipt URL
   * @returns {Promise<Object>} { period_completed: boolean }
   */
  const payDespesa = async (despesaId: number, franchiseId: number, paymentDate: string, comprovanteUrl: string | null = null) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollCalculations', 'payDespesa', 'Paying despesa', {
      despesaId,
      franchiseId,
      paymentDate,
      hasComprovante: !!comprovanteUrl
    });

    try {
      const payload: Payroll_PayDespesaInput = {
        franchise_id: franchiseId,
        payment_date: paymentDate,
        ...(comprovanteUrl !== null && { comprovante_url: comprovanteUrl })
      };

      validate('Payroll_PayDespesaInput', payload);
      const response = await httpService.post(`/payroll/pay_despesa/${despesaId}`, payload, {
        responseContract: 'Payroll_PayDespesaResponse',
        inputContract: 'Payroll_PayDespesaInput'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to pay despesa');
      }

      const result = response.dados;
      log('usePayrollCalculations', 'payDespesa', 'Despesa paid', {
        despesaId,
        periodCompleted: result?.period_completed
      });

      return result;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollCalculations', 'payDespesa', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    duePayments,
    availableExpenses,
    calculations,
    isLoading,
    error,

    // Methods
    discoverDuePayments,
    completePayment,
    getAvailableExpenses,
    linkExpenses,

    // Step 5: Payment Rules Conciliation - Sequential Flow
    // Step 0: Display payment rules
    getPaymentRulesForPeriod,
    // Step 1: Calculate commission over gross revenue EXECUTED (operador)
    calculateCommissionOverGrossExecuted,
    // Step 2: Calculate commission over gross revenue SOLD REMOTELY (atendente)
    calculateCommissionOverGrossSoldRemotely,
    // Step 3: Calculate fixed rules
    calculateFixed,
    // Step 4: Mark period as calculated (status = 3)
    markPeriodCalculated,
    // Step 5: Confirm and create despesas
    confirmPeriod,
    // Helper: Get calculation summary
    getPeriodCalculationSummary,
    // Post-confirm: Payment management
    getPeriodPayments,
    payDespesa
  };
}
